import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Wallet as WalletIcon, Coins, DollarSign, ArrowRight, ArrowDownToLine, Loader2,
  CheckCircle2, Clock, XCircle, Info, Sparkles, History,
} from 'lucide-react';

const METHODS = [
  { v: 'paypal',       label: 'PayPal',       helper: 'PayPal email address' },
  { v: 'cashapp',      label: 'Cash App',     helper: '$Cashtag' },
  { v: 'venmo',        label: 'Venmo',        helper: 'Venmo username' },
  { v: 'bank_transfer', label: 'Bank transfer', helper: 'Bank details' },
  { v: 'stripe_payout', label: 'Stripe',      helper: 'Account email' },
];

function StatusPill({ s }) {
  const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold';
  if (s === 'pending')   return <span className={`${base} bg-amber-500/15 text-amber-200 border border-amber-500/30`}><Clock className="w-3 h-3" /> Pending</span>;
  if (s === 'approved' || s === 'paid') return <span className={`${base} bg-emerald-500/15 text-emerald-200 border border-emerald-500/30`}><CheckCircle2 className="w-3 h-3" /> Paid</span>;
  if (s === 'rejected')  return <span className={`${base} bg-rose-500/15 text-rose-200 border border-rose-500/30`}><XCircle className="w-3 h-3" /> Rejected</span>;
  return <span className={`${base} bg-white/10 text-slate-300`}>{s}</span>;
}

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [showCashout, setShowCashout] = useState(false);
  const [method, setMethod] = useState('paypal');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, h] = await Promise.all([
      api.get('/jxp/summary'),
      api.get('/cashout/mine'),
    ]);
    setSummary(s.data); setHistory(h.data);
  };
  useEffect(() => { if (user) load(); }, [user]);

  const submit = async () => {
    if (!details.trim()) return toast.error('Payout details required');
    setBusy(true);
    try {
      const { data } = await api.post('/cashout/request', { method, details });
      toast.success(`Cash-out requested — $${data.payout_amount.toFixed(2)} on the way after review.`);
      setShowCashout(false); setDetails('');
      await load(); await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally { setBusy(false); }
  };

  if (!user) return <div className="p-10 text-center text-slate-400">Please sign in to view your wallet.</div>;
  if (!summary) return <div className="p-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  const { jxp, wallet_usd_balance, jxp_usd_value, total_usd,
          cashout_progress_pct, cashout_min_usd, cashout_fee_usd,
          jxp_to_usd_rate, eligible, payout_after_fee } = summary;
  const remaining = Math.max(0, cashout_min_usd - total_usd);

  return (
    <div className="max-w-5xl mx-auto space-y-5 pb-24">
      <header>
        <h1 className="font-display text-3xl font-black flex items-center gap-2">
          <WalletIcon className="w-7 h-7 text-amber-400" /> Your <span className="gradient-text">Wallet</span>
        </h1>
        <p className="text-sm text-slate-400">Earn JXP across Jixxeo AI. Cash out any time you reach ${cashout_min_usd.toFixed(2)}+.</p>
      </header>

      {/* Total balance + progress */}
      <Card className="glass p-6 relative overflow-hidden" data-testid="wallet-total-card">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-slate-400">Cashable balance</div>
          <div className="flex items-baseline gap-2 mt-1">
            <DollarSign className="w-9 h-9 text-amber-300 -mb-1" />
            <span data-testid="wallet-total-usd" className="font-display text-6xl font-black">{total_usd.toFixed(2)}</span>
            <span className="text-sm text-slate-500">USD</span>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">Progress to ${cashout_min_usd.toFixed(2)} payout</span>
              <span className="font-bold text-amber-300">{cashout_progress_pct}%</span>
            </div>
            <Progress data-testid="wallet-progress" value={cashout_progress_pct} className="h-2.5" />
            <div className="text-[11px] text-slate-500">
              {eligible
                ? <>✨ You're eligible! A ${cashout_fee_usd.toFixed(2)} fee applies; you'd receive <b className="text-emerald-300">${payout_after_fee.toFixed(2)}</b>.</>
                : <>${remaining.toFixed(2)} more to unlock cash-out. Keep earning — there's no cap, bigger balance means fewer fees per dollar.</>
              }
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl p-3 bg-white/5 border border-white/10" data-testid="wallet-jxp-card">
              <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Coins className="w-3 h-3" /> JXP earned</div>
              <div className="font-display text-2xl font-black text-amber-300">{jxp.toLocaleString()}</div>
              <div className="text-[10px] text-slate-500">= ${jxp_usd_value.toFixed(2)} ({jxp_to_usd_rate} JXP = $1)</div>
            </div>
            <div className="rounded-xl p-3 bg-white/5 border border-white/10" data-testid="wallet-usd-card">
              <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> USD balance</div>
              <div className="font-display text-2xl font-black text-emerald-300">${wallet_usd_balance.toFixed(2)}</div>
              <div className="text-[10px] text-slate-500">From marketplace sales, admin credits & rewards.</div>
            </div>
          </div>

          <Button
            data-testid="wallet-cashout-btn"
            disabled={!eligible}
            onClick={() => setShowCashout(true)}
            className={`w-full mt-5 h-12 font-bold ${eligible ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-slate-950 hover:scale-[1.01]' : 'bg-white/5 text-slate-500'}`}
          >
            {eligible
              ? <><ArrowDownToLine className="w-5 h-5 mr-2" /> Cash out ${payout_after_fee.toFixed(2)}</>
              : <>Reach ${cashout_min_usd.toFixed(2)} to cash out</>
            }
          </Button>
        </div>
      </Card>

      {/* Earn more / history */}
      <Tabs defaultValue="earn">
        <TabsList className="glass">
          <TabsTrigger value="earn" data-testid="wallet-tab-earn"><Sparkles className="w-3.5 h-3.5 mr-1" /> Earn more</TabsTrigger>
          <TabsTrigger value="history" data-testid="wallet-tab-history"><History className="w-3.5 h-3.5 mr-1" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="earn" className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { title: 'Daily missions', body: 'Finish 3 simple tasks every day for bonus JXP.', url: '/missions' },
            { title: 'Refer a friend', body: 'Earn JXP for every friend who signs up with your code.', url: '/profile' },
            { title: 'Sell on marketplace', body: 'Keep 90% of every prompt pack or AI artwork sale.', url: '/marketplace' },
            { title: 'Hit a 7-day streak', body: 'Big streak bonuses stack with daily missions.', url: '/' },
          ].map((e, i) => (
            <Card key={i} className="glass p-4 hover:border-violet-400/40 transition-colors">
              <div className="font-bold">{e.title}</div>
              <div className="text-xs text-slate-400 mt-1">{e.body}</div>
              <a href={e.url} className="mt-3 text-xs text-violet-300 font-semibold flex items-center gap-1 hover:underline">
                Open <ArrowRight className="w-3 h-3" />
              </a>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-2">
          {history.length === 0 && (
            <Card className="glass p-10 text-center text-sm text-slate-500">
              No cash-out requests yet.
            </Card>
          )}
          {history.map(h => (
            <Card key={h.id} className="glass p-3 flex items-center justify-between" data-testid={`cashout-${h.id}`}>
              <div>
                <div className="font-bold">${(h.payout_amount ?? (h.usd_amount - (h.fee || 0))).toFixed(2)} via {METHODS.find(m => m.v === h.method)?.label || h.method}</div>
                <div className="text-[11px] text-slate-500">
                  {new Date(h.created_at).toLocaleString()} · gross ${h.usd_amount.toFixed(2)} · fee ${h.fee.toFixed(2)}
                </div>
              </div>
              <StatusPill s={h.status} />
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Cashout dialog */}
      <Dialog open={showCashout} onOpenChange={(o) => !o && setShowCashout(false)}>
        <DialogContent data-testid="cashout-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cash out ${total_usd.toFixed(2)}</DialogTitle>
            <DialogDescription>You'll receive <b className="text-emerald-300">${payout_after_fee.toFixed(2)}</b> after a ${cashout_fee_usd.toFixed(2)} processing fee. Reviews usually take 1–3 business days.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Payout method</Label>
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map(m => (
                  <button key={m.v} data-testid={`cashout-method-${m.v}`} onClick={() => setMethod(m.v)}
                    className={`p-2 rounded-lg border text-xs font-bold ${method === m.v ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200' : 'border-white/10 text-slate-300'}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">{METHODS.find(m => m.v === method)?.helper}</Label>
              <Textarea data-testid="cashout-details" rows={2} value={details} onChange={e => setDetails(e.target.value)} placeholder="Enter your payout details" />
            </div>
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-2.5 text-[11px] text-amber-200 flex gap-2">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Your JXP and USD balance will be frozen while we review this request. You'll get a notification when it's paid.
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="cashout-submit" onClick={submit} disabled={busy} className="w-full gradient-brand text-white">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : `Request $${payout_after_fee.toFixed(2)} payout`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
